/**
 * Gallery — every world in the library rendering LIVE in a grid. By default the tiles run on
 * the engine's synthetic groove (no audio needed); drop/choose a track and ALL tiles react to
 * the same music side-by-side — the fastest way to compare worlds. Click a tile to focus it
 * fullscreen through the full PostFX finishing pass.
 *
 * Each tile attaches a mode instance directly to its own small canvas (no Director — the world
 * is forced via worldRenderParams). The focused view uses the real VisualHost with a hand-built
 * DirectorState, so what you see fullscreen is exactly what the Director would render.
 */
import React, { useEffect, useRef, useState } from 'react'
import type { DirectorState, Membership, MusicalFrame, VisualMode, VisualHost, World } from '@syqel/engine'
import { setTilePreview } from '@syqel/worlds-three'
import { Engine, otaModeFactory } from '@syqel/ota-client'
import { track } from './analytics'
import { FAVORITES_FILTER, VOLUMES, galleryWorlds, offeredVolumes, refreshEntitlement, toggleFullscreen, type GalleryFilter } from './catalogue'
import { favorites } from './favorites'
import { loadFile as sessionLoadFile, loadStems, onSource, sourceLabel } from './audioSession'
import { stereo } from './audio/graph'
import { readStems } from './audio/stems'
import { SourcePicker } from './SourcePicker'
import { SubscribeModal } from './SubscribeModal'
import { onCastTargetChange } from './cast/castTarget'
import { watchCastStatus } from './cast/sender'
import { worldNotes } from './worldNotes'
import { onHeavyChange } from './worldCost'

const TILE_W = 480 // fixed internal resolution per tile (cheap + crisp enough)
const TILE_H = 270
const FLAT: Membership = { mainstream: 0.2, acoustic: 0.2, bassGroove: 0.2, mellow: 0.2, extreme: 0.2 }

function forcedState(world: World, intensity: number, density: number, richness: number): DirectorState {
  return {
    archetype: world.archetype, membership: FLAT, world, nextWorld: null, blend: 0,
    intensity, density, richness, buildArc: 0,
    render: Engine.worldRenderParams(world, intensity, density, richness)
  }
}

const btn: React.CSSProperties = {
  padding: '8px 14px', fontSize: 12, fontFamily: 'monospace', color: '#fff',
  background: 'rgba(124,91,255,0.35)', border: '1px solid rgba(124,91,255,0.7)',
  borderRadius: 8, cursor: 'pointer'
}

/**
 * A gallery tile only holds a LIVE renderer while it's "on stage". Browsers cap active
 * WebGL contexts (~16); with a 24-world catalogue, one context per tile killed the gallery
 * (context-lost cascade). Owner's call on the UX: the spotlight sweeps ONE COLUMN at a
 * time — that column animates for a few seconds while every other tile holds its latest
 * frame as a still; when the spotlight moves on, contexts are explicitly released. The
 * first pass runs fast so the whole grid gets real stills within seconds.
 */
interface Tile {
  world: World
  view: CanvasRenderingContext2D | null
  live: { mode: VisualMode; canvas: HTMLCanvasElement } | null
  since: number
  everLive: boolean
  failed: boolean
}

const MAX_LIVE = 10 // hard cap on live tiles (contexts in flight stay well under ~16)
const COL_SEC = 4.2 // how long the spotlight column animates
const FIRST_PASS_SEC = 1.1 // faster first lap, so every tile gets a real still quickly

export function Gallery(): JSX.Element {
  const gridRef = useRef<HTMLDivElement>(null)
  const focusCanvasRef = useRef<HTMLCanvasElement>(null)
  const liveRef = useRef(sourceLabel() !== '')
  const focusRef = useRef<World | null>(null)
  const [loaded, setLoaded] = useState(sourceLabel())
  const [showSupport, setShowSupport] = useState(false)
  // which lane the grid shows: an offered volume (VOLUMES id) or Experiments (no volume)
  const [vol, setVol] = useState<GalleryFilter>(VOLUMES[0].id)
  // ?world=<id> auto-focuses a world fullscreen (the screenshot-iteration hook). It looks
  // up the FULL library, not just the visible lane — the owner's door to any world by direct URL.
  const [focus, setFocus] = useState<World | null>(() => {
    const id = new URLSearchParams(window.location.search).get('world')
    return Engine.WORLDS.find((w) => w.id === id) ?? null
  })
  // re-render on every star/un-star; under the favorites filter the tile SET is the favorites list, so
  // the tile machinery below is rebuilt when it changes (its canvases are matched to tiles by index)
  const [, setFavRev] = useState(0)
  useEffect(() => favorites.subscribe(() => setFavRev((n) => n + 1)), [])
  // ...and on this device's heavy verdicts and the pushed TV's offer (desktop): the tiles mark what it can't show, from the page's one status
  // poll (cast/sender.ts) — the same marks the player's world select shows (worldNotes.ts)
  useEffect(() => onCastTargetChange(() => setFavRev((n) => n + 1)), [])
  useEffect(() => onHeavyChange(() => setFavRev((n) => n + 1)), [])
  useEffect(() => watchCastStatus(() => {}), [])
  const laneKey = vol === FAVORITES_FILTER ? `${vol}:${favorites.list().join(',')}` : vol
  // ?bare hides the focus nav/HUD — a clean frame for App Store screenshots.
  const bare = new URLSearchParams(window.location.search).has('bare')

  useEffect(() => { focusRef.current = focus }, [focus])
  useEffect(() => onSource((l) => { setLoaded(l); liveRef.current = l !== '' }), [])

  useEffect(() => {
    // detectSources: the engine stamps frame.detected + frame.sources every update (SDK 0.8)
    const music = new Engine.MusicalAnalyzer({ detectSources: true })
    const synth = new Engine.SyntheticMusic()

    // one tile per world in the selected lane; visible canvases are 2D blit targets, NOT WebGL surfaces
    const tiles: Tile[] = galleryWorlds(vol).map((world) => ({ world, view: null, live: null, since: 0, everLive: false, failed: false }))
    const grid = gridRef.current
    if (grid) {
      const canvases = grid.querySelectorAll('canvas')
      tiles.forEach((tile, i) => {
        const canvas = canvases[i] as HTMLCanvasElement | undefined
        if (!canvas) return
        tile.view = canvas.getContext('2d')
        if (tile.view) {
          // first paint: the world's own night colour, so the grid reads before its turn
          const { r, g, b } = tile.world.palette.bg
          tile.view.fillStyle = `rgb(${r},${g},${b})`
          tile.view.fillRect(0, 0, TILE_W, TILE_H)
        }
      })
    }

    // group tiles into COLUMNS by their canvas x-position (the grid is auto-fill)
    const columnsOf = (): number[][] => {
      const cols = new Map<number, number[]>()
      tiles.forEach((_tile, i) => {
        const el = grid?.querySelectorAll('canvas')[i] as HTMLCanvasElement | undefined
        if (!el) return
        const key = Math.round(el.getBoundingClientRect().left)
        const arr = cols.get(key) ?? []
        arr.push(i)
        cols.set(key, arr)
      })
      return [...cols.entries()].sort((a, b) => a[0] - b[0]).map(([, v]) => v)
    }
    let activeCol = -1
    let colTimer = 1e9 // trigger the first column immediately
    let firstPass = true
    const releaseContext = (canvas: HTMLCanvasElement): void => {
      const gl = (canvas.getContext('webgl2') ?? canvas.getContext('webgl')) as WebGLRenderingContext | null
      gl?.getExtension('WEBGL_lose_context')?.loseContext()
    }
    const retire = (tile: Tile): void => {
      if (!tile.live) return
      try {
        tile.live.mode.dispose()
      } catch {
        /* context may already be lost */
      }
      releaseContext(tile.live.canvas)
      tile.live = null
    }
    const goLive = (tile: Tile, nowSec: number): void => {
      const canvas = document.createElement('canvas')
      canvas.width = TILE_W
      canvas.height = TILE_H
      const mode = otaModeFactory(tile.world)
      setTilePreview(true) // tiles skip the env bake + shadows: attach stays cheap
      try {
        mode.attach(canvas)
        tile.live = { mode, canvas }
        tile.everLive = true
      } catch (err) {
        tile.failed = true
        console.warn(`[syqel] gallery tile '${Engine.modeKey(tile.world)}' failed:`, err)
      } finally {
        setTilePreview(false)
      }
      tile.since = nowSec
    }
    const spotlightNext = (nowSec: number): void => {
      const cols = columnsOf()
      if (cols.length === 0) return
      for (const tile of tiles) retire(tile)
      activeCol += 1
      if (activeCol >= cols.length) {
        activeCol = 0
        firstPass = false
      }
      for (const idx of cols[activeCol].slice(0, MAX_LIVE)) {
        const tile = tiles[idx]
        if (!tile.failed && tile.view) goLive(tile, nowSec)
      }
      colTimer = 0
    }

    // the focused fullscreen view goes through the real host (with PostFX)
    let host: VisualHost | null = null
    let hostFor: string | null = null
    // adaptive resolution for the focused view (the heavy, player-equivalent surface) — native on
    // capable machines, scales down only under sustained low fps; opt-out via 'syqel.adapt'='0'.
    const adapt = (() => { try { return localStorage.getItem('syqel.adapt') !== '0' } catch { return true } })()
    const perfGov = adapt ? new Engine.PerfGovernor() : null

    let raf = 0
    let disposed = false
    let last = performance.now()

    const loop = (): void => {
      const now = performance.now()
      const realDt = (now - last) / 1000
      const dt = Math.min(0.05, realDt)
      last = now
      const t = now / 1000
      const quality = perfGov ? perfGov.tick(realDt) : 1 // applied to the focused host below

      const frame: MusicalFrame = liveRef.current ? music.updateFromAnalyser(stereo, t) : synth.frame(t)
      if (liveRef.current) {
        // frame.detected + frame.sources come from the analyzer (detectSources: true).
        // stem fusion — isolated instruments replace voices/panEvents when a stem session is live;
        // real stems also override the engine's estimated sources (provenance renders bold).
        const fusion = readStems(t)
        if (fusion) {
          frame.voices = fusion.voices
          frame.panEvents = fusion.panEvents
          frame.stems = fusion.stems
          frame.sources = Engine.sourcesFromStems(fusion.stems)
        }
      }
      const intensity = frame.cal.energy
      const density = frame.cal.onsetDensity
      const richness = Engine.richnessOf(frame.cal)
      const focused = focusRef.current

      if (focused) {
        // the focused host needs headroom — clear the stage entirely
        for (const tile of tiles) retire(tile)
        const canvas = focusCanvasRef.current
        if (canvas) {
          if (hostFor !== focused.id) {
            host?.dispose()
            host = new Engine.VisualHost({
              modeFactory: otaModeFactory,
              onModeError: (key, err) => console.warn(`[syqel] renderer '${key}' failed, using fallback:`, err)
            })
            host.attach(canvas)
            hostFor = focused.id
          }
          const dpr = window.devicePixelRatio || 1
          const w = Math.max(1, Math.floor(canvas.clientWidth * dpr * quality))
          const h = Math.max(1, Math.floor(canvas.clientHeight * dpr * quality))
          if (host) {
            host.update(frame, forcedState(focused, intensity, density, richness), dt, t, w, h)
            host.render()
          }
        }
      } else {
        if (hostFor) {
          host?.dispose()
          host = null
          hostFor = null
        }
        // the spotlight: one column animates at a time; the rest hold their stills
        const nowSec = now / 1000
        colTimer += dt
        if (colTimer > (firstPass ? FIRST_PASS_SEC : COL_SEC)) spotlightNext(nowSec)
        for (const tile of tiles) {
          if (!tile.live || !tile.view) continue
          tile.live.mode.update({
            frame, render: Engine.worldRenderParams(tile.world, intensity, density, richness),
            dt, timeSec: t, width: TILE_W, height: TILE_H
          })
          tile.live.mode.render()
          tile.view.drawImage(tile.live.canvas, 0, 0) // the latest frame doubles as its poster
        }
      }

      if (!disposed) raf = requestAnimationFrame(loop)
    }
    raf = requestAnimationFrame(loop)

    return () => {
      disposed = true
      cancelAnimationFrame(raf)
      for (const tile of tiles) retire(tile)
      host?.dispose()
      // the audio session is app-wide on purpose — leaving the gallery must not stop the music
    }
    // rebuild the tile/spotlight machinery when the lane filter changes (new world set + canvases)
  }, [laneKey])

  const loadFile = (file: File): void => {
    sessionLoadFile(file)
  }

  const openWorld = (world: World): void => {
    track('select_world', { world_id: world.id, surface: 'gallery' })
    try { sessionStorage.setItem('syqel.scene', world.id) } catch { /* private mode */ }
    window.location.hash = '' // back to the player, pinned to this world
  }

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: '#06060a', overflowY: 'auto' }}
      onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'copy' }}
      onDrop={(e) => {
        e.preventDefault()
        const files = Array.from(e.dataTransfer?.files ?? [])
        if (files.length > 1) void loadStems(files)
        else if (files[0]) loadFile(files[0])
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '14px 18px', font: '12px monospace', color: '#fff', flexWrap: 'wrap' }}>
        <a href="#" style={{ fontFamily: "'Changa', sans-serif", fontWeight: 700, fontSize: 20, letterSpacing: 3, color: '#fff', textDecoration: 'none', opacity: 0.95 }}>
          SYQEL<span style={{ fontSize: 9, verticalAlign: 'super' }}>®</span> Art
        </a>
        <span style={{ opacity: 0.5 }}>world gallery</span>
        <select
          value={vol}
          title="filter worlds by volume"
          onChange={(e) => setVol(e.target.value as GalleryFilter)}
          style={{ background: '#15151f', color: '#fff', border: '1px solid rgba(255,255,255,0.2)', borderRadius: 6, padding: '5px 8px', font: '12px monospace', cursor: 'pointer' }}
        >
          {offeredVolumes().map((v) => <option key={v.id} value={v.id}>{v.title} ({galleryWorlds(v.id).length})</option>)}
          <option value="exp">Experiments ({galleryWorlds('exp').length})</option>
          <option value={FAVORITES_FILTER}>★ Favorites ({galleryWorlds(FAVORITES_FILTER).length})</option>
        </select>
        <SourcePicker variant="bar" />
        <button style={{ ...btn, background: 'rgba(255,120,160,0.22)', border: '1px solid rgba(255,120,160,0.5)' }} onClick={() => setShowSupport(true)}>
          🔥 Remove watermark
        </button>
        <div style={{ opacity: 0.45 }}>{loaded ? `♪ ${loaded}` : 'synthetic groove'} · click a tile to focus</div>
      </div>

      <div
        ref={gridRef}
        style={{
          // a fixed 6-column grid: each lane caps at 24 worlds, so the grid is at most 6×4 and
          // fits the window without scrolling
          display: 'grid', gap: 12, padding: '0 18px 18px',
          gridTemplateColumns: 'repeat(6, minmax(0, 1fr))'
        }}
      >
        {galleryWorlds(vol).map((world) => (
          <div
            key={world.id}
            data-world-id={world.id}
            onClick={() => openWorld(world)}
            style={{ cursor: 'pointer', borderRadius: 10, overflow: 'hidden', border: '1px solid rgba(255,255,255,0.10)', background: '#000' }}
          >
            <canvas width={TILE_W} height={TILE_H} style={{ width: '100%', display: 'block', aspectRatio: '16 / 9' }} />
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '7px 10px', font: '11px monospace', color: '#fff' }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
                <button
                  title={favorites.has(world.id) ? 'remove from favorites' : 'add to favorites'}
                  aria-pressed={favorites.has(world.id)}
                  onClick={(e) => { e.stopPropagation(); const on = favorites.toggle(world.id); track('favorite', { world_id: world.id, on, surface: 'gallery' }) }}
                  style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', font: '13px monospace', lineHeight: 1, color: favorites.has(world.id) ? '#ffc860' : '#fff', opacity: favorites.has(world.id) ? 1 : 0.6 }}
                >{favorites.has(world.id) ? '★' : '☆'}</button>
                <span style={{ opacity: 0.95 }}>{world.name}</span>
                {worldNotes(world.id).map((n) => (
                  <span key={n} data-world-note style={{ opacity: 0.6, color: '#ffb48a', whiteSpace: 'nowrap' }}>· {n}</span>
                ))}
              </span>
              <span style={{ opacity: 0.5 }}>{Engine.modeKey(world)} · {world.archetype}</span>
            </div>
          </div>
        ))}
      </div>

      {showSupport && (
        <SubscribeModal
          reason="support"
          onSubscribed={() => { void refreshEntitlement().then(() => setShowSupport(false)) }}
          onClose={() => setShowSupport(false)}
        />
      )}
      {focus && (
        <div style={{ position: 'fixed', inset: 0, background: '#000', zIndex: 10 }} onClick={() => setFocus(null)}>
          <canvas ref={focusCanvasRef} style={{ width: '100%', height: '100%', display: 'block' }} />
          {!bare && (
            <div style={{ position: 'absolute', top: 14, left: 18, font: '13px monospace', color: '#fff', display: 'flex', gap: 12, alignItems: 'center' }}>
              <button style={btn} onClick={(e) => { e.stopPropagation(); setFocus(null) }}>← gallery</button>
              <span style={{ opacity: 0.9 }}>{focus.name}</span>
              <span style={{ opacity: 0.5 }}>{Engine.modeKey(focus)} · {focus.archetype} · full PostFX</span>
              <button title="fullscreen" onClick={(e) => { e.stopPropagation(); toggleFullscreen() }} style={btn}>⛶</button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
